import {Options, TooltipFormatterContextObject} from 'highcharts';

import {ChartOptionInterface} from '@veneer/core';

import {useWolfTheme} from '@br/mg-components';

import {IntlFormatter, useIntlFormatter} from '../../i18n';

import {BarChartProps, ChartProps, DonutChartProps} from './types';

export const useTooltipFormatter = (type: ChartProps['type'], formatXValue?: (x: number) => string) => {
    const {formatNumber} = useIntlFormatter();
    const theme = useWolfTheme();

    const styles = {
        // https://github.azc.ext.hp.com/veneer/veneer/blob/19a4ed4ee64589befdcc91669ae7b7c190ded641/packages/highcharts_interface/src/scripts/utils/commons.js#L91-L92
        // eslint-disable-next-line i18next/no-literal-string
        tooltipTitleStyle: `font-size: ${theme.fontSizeThree};`,
        // eslint-disable-next-line i18next/no-literal-string
        tooltipSubtitleStyle: `font-size: ${theme.fontSizeThree};`,
    };

    switch (type) {
        case 'donut':
            /* istanbul ignore next */
            return function (this: TooltipFormatterContextObject) {
                // eslint-disable-next-line i18next/no-literal-string
                return `<div style="padding: ${theme.orbitalOne} ${theme.orbitalTwo}; min-width: 80px">
                    <span style="${styles.tooltipTitleStyle}">${this.series.name}</span>
                    <div style="${
                        styles.tooltipSubtitleStyle
                    }; display: grid; grid-template-columns: 1fr 1fr 1fr; column-gap: ${theme.orbitalTwo}">
                    <span style="font-size: 1.5rem; color: ${/* eslint-disable @typescript-eslint/no-base-to-string */ this.color.toString() /* eslint-enable @typescript-eslint/no-base-to-string */};">&#x25CF;</span>
                    <span>${this.key} </span>
                    <span>${formatNumber(this.y)}</span>
                    </div>
                </div>`;
            };
        case 'bar':
            /* istanbul ignore next */
            return function (this: TooltipFormatterContextObject) {
                const points =
                    this.points?.map(point =>
                        // eslint-disable-next-line @typescript-eslint/no-base-to-string
                        `<span style="font-size: 1.5rem; color: ${point.color.toString()};">&#x25CF;</span>
                        <span>${point.series.name} </span>
                        <span>${formatNumber(point.y)}</span>
                      `.trim()
                    ) || [];
                // eslint-disable-next-line i18next/no-literal-string
                return `<div style="padding: ${theme.orbitalTwo} ${theme.orbitalTwo}; min-width: 80px">
                    <span style="${styles.tooltipTitleStyle}">${formatXValue ? formatXValue(this.x) : this.x}</span>
                    <div style="${
                        styles.tooltipSubtitleStyle
                    }; display: grid; grid-template-columns: 1fr 1fr 1fr; column-gap: ${theme.orbitalTwo}">
                    ${points.join('')}
                    </div>
                </div>`;
            };
        default:
            return;
    }
};

export const useChartOptions = (
    graphWidth: number,
    graphHeight: number,
    props: ChartProps
): Omit<ChartOptionInterface, keyof Options> & Options => {
    const {formatNumber} = useIntlFormatter();

    switch (props.type) {
        case 'donut':
            return formatDonutChartOptions(graphWidth, graphHeight, props);
        case 'bar':
            return formatBarChartOptions(graphHeight, props, formatNumber);
        default:
            return formatCustomOptions(props.options, graphWidth, graphHeight);
    }
};

const formatCustomOptions = (options: Options, graphWidth: number, graphHeight: number) => {
    /* istanbul ignore else */
    if (options.chart) {
        options.chart.height = graphHeight;
    }
    return options;
};

const formatDonutChartOptions = (
    graphWidth: number,
    graphHeight: number,
    props: DonutChartProps
): Omit<ChartOptionInterface, keyof Options> & Options => {
    const {legendWidth = 150, data, color, title, legendPosition = 'left'} = props;

    const padding = 0.85; // %

    const chartSize = graphWidth - legendWidth;
    // Depends on MutationObserver and object width / height. Not supported by jest.
    /* istanbul ignore next */
    const finalChartSize = chartSize > graphHeight ? graphHeight : chartSize;
    const chartSizeWithPadding = finalChartSize * padding;

    const chartData = data.map((d, idx) => ({
        name: d.name,
        y: d.value,
        color: color[idx],
    }));

    return {
        chart: {
            type: props.type,
            animation: false,
            events: {
                load() {
                    this.series.forEach(series => {
                        series.points.forEach(point => {
                            // @ts-expect-error it's there
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                            const graphic: SVGElement = point.graphic.element;
                            graphic.dataset.chart = title;
                            graphic.dataset.series = series.name;
                            graphic.dataset.category = point.name;
                            graphic.dataset.value = point.y?.toString();
                        });
                    });
                },
            },
            height: graphHeight,
        },
        legend: {
            enabled: true,
            itemMarginBottom: 8,
            ...(legendPosition === 'left'
                ? {
                      width: legendWidth,
                      layout: 'vertical',
                      align: 'left',
                      verticalAlign: 'middle',
                  }
                : /* istanbul ignore next */ {}),
        },
        plotOptions: {
            pie: {
                size: chartSizeWithPadding,
                showInLegend: true,
                animation: false,
            },
        },
        series: [
            {
                name: title,
                type: 'pie',
                data: chartData.every(d => d.y === 0) ? [] : chartData,
            },
        ],
    };
};

const formatBarChartOptions = (
    graphHeight: number,
    props: BarChartProps,
    formatNumber: IntlFormatter['formatNumber']
): Omit<ChartOptionInterface, keyof Options> & Options => {
    const {title, data, series, color, onClick} = props;

    return {
        chart: {
            type: 'stackedbar',
            animation: false,
            events: {
                load() {
                    this.series.forEach(series => {
                        series.points.forEach(point => {
                            // @ts-expect-error it's there
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                            const graphic: SVGElement = point.graphic.element;
                            graphic.dataset.chart = title;
                            graphic.dataset.series = series.name;
                            graphic.dataset.category = point.category;
                            graphic.dataset.value = point.y?.toString();
                        });
                    });
                },
            },
            height: graphHeight,
        },
        xAxis: {
            categories: data.map(d => d[1]),
        },
        yAxis: {
            labels: {
                formatter() {
                    return formatNumber(this.value as number);
                },
            },
        },
        legend: {
            itemMarginBottom: 8,
        },
        plotOptions: {
            series: {
                stacking: 'normal',
                animation: false,
            },
        },

        series: series.map((name, idx) => ({
            name,
            type: 'bar',
            data: data.map(d => d[idx + 2]),
            color: color[idx],
            events: {
                click: onClick,
            },
        })),
    };
};
